import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Phone, Star, MessageCircle, AlertTriangle, 
  ThumbsUp, ThumbsDown, CheckCircle, Send,
  FileText
} from 'lucide-react';
import { Badge } from '../ui/badge';

const ElisePatientExperienceSlide = () => {
  const [activeBranch, setActiveBranch] = useState('none'); // 'positive', 'negative'

  useEffect(() => {
    const cycle = async () => {
        while (true) {
            setActiveBranch('none');
            await new Promise(r => setTimeout(r, 1000));
            setActiveBranch('positive');
            await new Promise(r => setTimeout(r, 4000));
            setActiveBranch('none');
            await new Promise(r => setTimeout(r, 1000));
            setActiveBranch('negative');
            await new Promise(r => setTimeout(r, 4000));
        }
    };
    cycle();
  }, []);

  return (
    <div className="h-full w-full bg-slate-50 flex items-center justify-center p-12 relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none">
             <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-yellow-100/40 rounded-full blur-[80px] -translate-y-1/2 translate-x-1/2" />
             <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-indigo-100/40 rounded-full blur-[80px] translate-y-1/2 -translate-x-1/2" />
        </div>

        {/* Floating Agent Header (Top Left) */}
        <div className="absolute top-8 left-8 lg:left-12 z-50 flex items-center gap-4 bg-white/40 backdrop-blur-md p-2 pr-6 rounded-full border border-white/20 shadow-lg">
            <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-white shadow-sm">
                 <img src="/assets/elise.png" alt="Elise" className="w-full h-full object-cover" />
            </div>
            <div className="text-left">
                <h3 className="text-lg font-bold text-slate-900 leading-none">Elise</h3>
                <p className="text-xs text-blue-600 font-bold uppercase tracking-wide">Patient Experience</p>
            </div>
        </div>

        <div className="flex flex-row h-full w-full max-w-[1600px] gap-8 relative z-10 pt-0">
            {/* Left Side: Context */}
            <div className="w-1/3 flex flex-col justify-center space-y-8">
                
                <div className="space-y-4">
                    <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 leading-tight">
                        Uses AI voice calls to get you <span className="text-blue-600">5-star reviews</span>.
                    </h2>
                    
                    <p className="text-lg text-slate-600 leading-relaxed">
                        Elise calls patients after their appointment to ask about their experience. Her conversational AI voice converts <span className="font-bold">significantly better than SMS surveys</span>.
                        <br/><br/>
                        If they loved it, she texts them a Google Review link. If they didn't, she gathers detailed feedback for you to improve.
                    </p>
                </div>

                <div className="flex gap-4">
                    <div className="flex-1 p-4 bg-white rounded-xl border border-slate-200 shadow-sm">
                        <div className="text-2xl font-bold text-slate-900 mb-1">4.9</div>
                        <div className="text-xs text-slate-500 uppercase font-bold tracking-wide">Avg Rating</div>
                    </div>
                </div>
            </div>

            {/* Right Side: Animated Flowchart */}
            <div className="w-2/3 h-full flex items-center justify-center">
                <div className="w-full max-w-3xl relative">
                    
                    {/* Central Stem */}
                    <div className="flex flex-col items-center gap-8 mb-8">
                        {/* Step 1: Appointment Done */}
                        <motion.div 
                            className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex items-center gap-4 z-10 relative"
                            initial={{ y: -20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                        >
                            <div className="bg-slate-100 p-2 rounded-full"><CheckCircle className="w-5 h-5 text-slate-600" /></div>
                            <span className="font-bold text-slate-900">Appointment Completed</span>
                        </motion.div>

                        <div className="h-8 w-0.5 bg-slate-300" />

                        {/* Step 2: Elise Calls */}
                        <motion.div 
                            className="bg-blue-600 text-white p-6 rounded-2xl shadow-lg flex items-center gap-4 z-10 relative"
                            animate={{ scale: [1, 1.05, 1] }}
                            transition={{ duration: 2, repeat: Infinity }}
                        >
                            <div className="bg-white/20 p-3 rounded-full"><Phone className="w-6 h-6 text-white" /></div>
                            <div>
                                <div className="font-bold text-lg">Elise Calls Patient</div>
                                <div className="text-blue-100 text-sm">"How was your visit today?"</div>
                            </div>
                        </motion.div>

                        {/* Split Arrows */}
                        <div className="relative w-full h-8">
                            <svg className="absolute top-0 left-0 w-full h-full overflow-visible">
                                <path d="M 384 0 L 384 15 L 180 15 L 180 32" fill="none" stroke="#cbd5e1" strokeWidth="2" strokeDasharray="4 4" />
                                <path d="M 384 0 L 384 15 L 580 15 L 580 32" fill="none" stroke="#cbd5e1" strokeWidth="2" strokeDasharray="4 4" />
                            </svg>
                        </div>
                    </div>

                    {/* Branches */}
                    <div className="grid grid-cols-2 gap-8">
                        
                        {/* Positive Branch */}
                        <motion.div 
                            className={`flex flex-col items-center space-y-6 transition-all duration-500 ${activeBranch === 'positive' ? 'opacity-100 scale-105' : 'opacity-40 scale-100 blur-[1px]'}`}
                        >
                            <div className="bg-green-50 border border-green-200 p-4 rounded-xl flex flex-col items-center text-center w-full">
                                <ThumbsUp className="w-8 h-8 text-green-500 mb-2" />
                                <div className="font-bold text-green-900">Amazing Experience</div>
                                <div className="text-xs text-green-700">"It was great!"</div>
                            </div>

                            <div className="h-8 w-0.5 bg-green-200" />

                            <div className="bg-white border border-green-200 shadow-md p-4 rounded-xl flex flex-col items-center text-center w-full relative overflow-hidden">
                                <div className="absolute top-0 left-0 w-full h-1 bg-green-500" />
                                <Send className="w-6 h-6 text-blue-500 mb-2" />
                                <div className="font-bold text-slate-900">SMS Review Link Sent</div>
                                <div className="text-xs text-slate-500">"Click here to leave a review..."</div>
                            </div>

                            {activeBranch === 'positive' && (
                                <motion.div 
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    className="flex gap-1"
                                >
                                    {[1,2,3,4,5].map(i => (
                                        <Star key={i} className="w-8 h-8 text-yellow-400 fill-yellow-400 drop-shadow-sm" />
                                    ))}
                                </motion.div>
                            )}
                        </motion.div>

                        {/* Negative Branch */}
                        <motion.div 
                            className={`flex flex-col items-center space-y-6 transition-all duration-500 ${activeBranch === 'negative' ? 'opacity-100 scale-105' : 'opacity-40 scale-100 blur-[1px]'}`}
                        >
                            <div className="bg-amber-50 border border-amber-200 p-4 rounded-xl flex flex-col items-center text-center w-full">
                                <ThumbsDown className="w-8 h-8 text-amber-500 mb-2" />
                                <div className="font-bold text-amber-900">Lukewarm / Bad</div>
                                <div className="text-xs text-amber-700">"Could be better..."</div>
                            </div>

                            <div className="h-8 w-0.5 bg-amber-200" />

                            <div className="bg-white border border-amber-200 shadow-md p-4 rounded-xl flex flex-col items-center text-center w-full relative overflow-hidden">
                                <div className="absolute top-0 left-0 w-full h-1 bg-amber-500" />
                                <MessageCircle className="w-6 h-6 text-amber-500 mb-2" />
                                <div className="font-bold text-slate-900">Collects Detailed Feedback</div>
                                <div className="text-xs text-slate-500">"What could we improve?"</div>
                            </div>

                             {activeBranch === 'negative' && (
                                <motion.div 
                                    initial={{ y: 10, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    className="bg-slate-800 text-white text-xs p-3 rounded-lg shadow-lg flex items-center gap-2"
                                >
                                    <FileText className="w-4 h-4" />
                                    <span>Feedback Report Generated</span>
                                </motion.div>
                            )}
                        </motion.div>

                    </div>

                </div>
            </div>
        </div>
    </div>
  );
};

export default ElisePatientExperienceSlide;
